import React, { useState } from 'react';
import { COMPETITIVENESS, TYPE_LABEL } from '../format';
import { Eligibility, Filters, TopicGroup } from '../types';

export const DEFAULT_FILTERS: Filters = {
  q: '',
  when: '',
  types: [],
  competitiveness: [],
  topics: [],
  pays_only: false,
  free_only: false,
  aid_only: false,
  cost_max: '',
  include_unpriced: true,
  delivery: [],
  housing: [],
  grades: [],
  near_zip: '',
  near_miles: 25,
  commute_only: false,
  open_only: false,
  open_to_me: false,
  group_family: true,
  sort: 'deadline',
};

interface Props {
  value: Filters;
  onChange: (f: Filters) => void;
  topics: TopicGroup[];
  eligibility: Eligibility | null;
  /** Who the results are for, e.g. "Maya" on the counselor and parent tabs. */
  forName?: string;
  /** The student's zip from her profile, offered for "Near me" (never applied on its own). */
  profileZip?: string | null;
}

function toggle<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function Chips({ options, selected, onToggle, label }: { options: [string, string][]; selected: string[]; onToggle: (v: string) => void; label: string }) {
  return (
    <div className="row" role="group" aria-label={label}>
      {options.map(([v, l]) => (
        <button key={v} type="button" className={`chipb ${selected.includes(v) ? 'on' : ''}`} aria-pressed={selected.includes(v)} onClick={() => onToggle(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}

export default function FiltersPanel({ value: f, onChange, topics, eligibility, forName, profileZip }: Props) {
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<Filters>) => onChange({ ...f, ...patch });
  const active =
    (f.when ? 1 : 0) + f.types.length + f.competitiveness.length + f.topics.length + f.delivery.length + f.housing.length + f.grades.length +
    [f.pays_only, f.free_only, f.aid_only, !!f.cost_max, /^\d{5}$/.test(f.near_zip), f.open_only, f.open_to_me].filter(Boolean).length;
  const eligKnown = !!eligibility && [eligibility.elig_age, eligibility.elig_state, eligibility.elig_citizenship, eligibility.elig_gender].some((v) => v != null);

  return (
    <aside className={`card filters ${open ? '' : 'closed'}`} aria-label="Filters">
      <div className="between ftoggle">
        <h3>Filters{active ? ` (${active})` : ''}</h3>
        <button type="button" className="btn sm" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? 'Hide filters' : 'Show filters'}
        </button>
      </div>
      <div className="fbody">
        <div className="fgroup">
          <span className="lbl">Open to {forName || 'me'}</span>
          <label className="check">
            <input type="checkbox" checked={f.open_to_me} onChange={(e) => set({ open_to_me: e.target.checked })} disabled={!eligKnown} />
            Only programs {forName ? `${forName} can` : 'I can'} apply to
          </label>
          {!eligKnown && <p className="muted small" style={{ marginTop: 6 }}>Add date of birth, state and citizenship in My Profile to use this.</p>}
          <label className="check" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={f.open_only} onChange={(e) => set({ open_only: e.target.checked })} />
            Hide programs whose deadline passed
          </label>
        </div>

        <div className="fgroup">
          <span className="lbl">When</span>
          <Chips label="When" options={[['summer', 'Summer'], ['year_round', 'Year-round']]} selected={f.when ? [f.when] : []} onToggle={(v) => set({ when: f.when === v ? '' : v })} />
        </div>

        <div className="fgroup">
          <span className="lbl">Grade this fall</span>
          <Chips
            label="Grade"
            options={[['9', '9'], ['10', '10'], ['11', '11'], ['12', '12']]}
            selected={f.grades.map(String)}
            onToggle={(v) => set({ grades: toggle(f.grades, Number(v)) })}
          />
        </div>

        <div className="fgroup">
          <span className="lbl">Near {forName ? forName : 'me'}</span>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input
              className="in"
              style={{ width: 96 }}
              inputMode="numeric"
              maxLength={5}
              placeholder="Zip"
              aria-label="Zip code"
              value={f.near_zip}
              onChange={(e) => set({ near_zip: e.target.value.replace(/\D/g, '').slice(0, 5) })}
            />
            <select className="in" aria-label="Distance" value={f.near_miles} onChange={(e) => set({ near_miles: Number(e.target.value) })}>
              {[10, 25, 50, 100].map((m) => (
                <option key={m} value={m}>{`${m} miles`}</option>
              ))}
            </select>
          </div>
          {profileZip && f.near_zip !== profileZip && (
            <button type="button" className="linkish small" style={{ marginTop: 6 }} onClick={() => set({ near_zip: profileZip })}>
              Use {forName ? `${forName}'s` : 'my'} zip, {profileZip}
            </button>
          )}
          <label className="check" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={f.commute_only} onChange={(e) => set({ commute_only: e.target.checked })} />
            Day programs {forName ? `${forName} can` : 'I can'} commute to
          </label>
        </div>

        <div className="fgroup">
          <span className="lbl">Type of program</span>
          <Chips label="Type of program" options={Object.entries(TYPE_LABEL)} selected={f.types} onToggle={(v) => set({ types: toggle(f.types, v) })} />
        </div>

        <div className="fgroup">
          <span className="lbl">How competitive</span>
          <Chips
            label="How competitive"
            options={Object.entries(COMPETITIVENESS).map(([k, v]) => [k, v.label] as [string, string])}
            selected={f.competitiveness}
            onToggle={(v) => set({ competitiveness: toggle(f.competitiveness, v) })}
          />
        </div>

        <div className="fgroup">
          <label className="lbl" htmlFor="ep-topic">Topics</label>
          <select
            id="ep-topic"
            className="in"
            value=""
            onChange={(e) => e.target.value && set({ topics: toggle(f.topics, e.target.value) })}
          >
            <option value="">{f.topics.length ? 'Add another topic' : 'Any topic'}</option>
            {topics.map((g) => (
              <optgroup key={g.key} label={g.label}>
                {g.topics.map((t) => (
                  <option key={t.slug} value={t.slug} disabled={f.topics.includes(t.slug)}>
                    {t.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {f.topics.length > 0 && (
            <div className="row" style={{ marginTop: 8 }}>
              {f.topics.map((slug) => {
                const name = topics.flatMap((g) => g.topics).find((t) => t.slug === slug)?.name ?? slug;
                return (
                  <button key={slug} type="button" className="chipb on" onClick={() => set({ topics: f.topics.filter((t) => t !== slug) })} aria-label={`Remove ${name}`}>
                    {name} ×
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="fgroup">
          <span className="lbl">Money</span>
          <label className="check"><input type="checkbox" checked={f.pays_only} onChange={(e) => set({ pays_only: e.target.checked })} />Pays students</label>
          <label className="check" style={{ marginTop: 6 }}><input type="checkbox" checked={f.free_only} onChange={(e) => set({ free_only: e.target.checked })} />Free to attend</label>
          <label className="check" style={{ marginTop: 6 }}><input type="checkbox" checked={f.aid_only} onChange={(e) => set({ aid_only: e.target.checked })} />Financial aid</label>
          <div className="row" style={{ marginTop: 10, flexWrap: 'nowrap' }}>
            <span className="muted small" style={{ whiteSpace: 'nowrap' }}>Up to</span>
            <select className="in" aria-label="Most you would pay" value={f.cost_max} onChange={(e) => set({ cost_max: e.target.value })}>
              <option value="">Any price</option>
              {[1000, 3000, 5000, 10000].map((n) => (
                <option key={n} value={n}>{`$${n.toLocaleString('en-US')}`}</option>
              ))}
            </select>
          </div>
          {f.cost_max && (
            <label className="check" style={{ marginTop: 8 }}>
              <input type="checkbox" checked={f.include_unpriced} onChange={(e) => set({ include_unpriced: e.target.checked })} />
              Include programs with cost on their website
            </label>
          )}
        </div>

        <div className="fgroup">
          <span className="lbl">Format</span>
          <Chips label="Format" options={[['in_person', 'In person'], ['online', 'Online'], ['hybrid', 'Hybrid']]} selected={f.delivery} onToggle={(v) => set({ delivery: toggle(f.delivery, v) })} />
        </div>

        <div className="fgroup">
          <span className="lbl">Housing</span>
          <Chips label="Housing" options={[['residential', 'Residential'], ['commuter', 'Commuter']]} selected={f.housing} onToggle={(v) => set({ housing: toggle(f.housing, v) })} />
        </div>

        {active > 0 && (
          <div className="fgroup">
            <button type="button" className="linkish" onClick={() => onChange({ ...DEFAULT_FILTERS, q: f.q, sort: f.sort, group_family: f.group_family })}>
              Clear filters
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
